import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getTerminologyItem,
  probeTerminology,
  updateTerminologyItem,
} from "@/api/terminology";
import type { TerminologyProbeResponse } from "@/api/terminology";
import { useToast } from "@/components/toast";

import { SenseSearch } from "../SenseSearch";
import type { SenseTarget } from "../senseSearchTypes";

export function DisambiguationPanel() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [picked, setPicked] = useState<SenseTarget | null>(null);
  const [ctx, setCtx] = useState("");
  const [probeText, setProbeText] = useState("");
  const [probeRes, setProbeRes] = useState<TerminologyProbeResponse | null>(null);
  const [probing, setProbing] = useState(false);

  const detail = useQuery({
    queryKey: ["terminology-item", picked?.item_id],
    queryFn: () => getTerminologyItem(picked!.item_id),
    enabled: !!picked,
  });
  const item = detail.data?.ok ? detail.data.data.item : null;

  useEffect(() => {
    if (item && item.item_type === "abbreviation") {
      setCtx((item.context_words_stems ?? []).join(", "));
    }
  }, [item]);

  const saveMut = useMutation({
    mutationFn: async () => {
      const r = await updateTerminologyItem(picked!.item_id, { context_words: ctx });
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    onSuccess: () => {
      toast("Контекст сохранён", "success");
      void qc.invalidateQueries({
        queryKey: ["terminology-item", picked?.item_id],
      });
    },
    onError: (e: Error) => toast(e.message, "error"),
  });

  async function probe() {
    if (!probeText.trim() || !picked) return;
    setProbing(true);
    setProbeRes(null);
    try {
      // Направление по скрипту входа: кириллица → RU→EN, иначе EN→RU. Было жёстко
      // EN→RU → русские амбигуэнтные аббревиатуры (HR→ОР/ЧСС, СД, АД) не матчились.
      const isCyrillic = /[А-Яа-яЁё]/.test(probeText);
      const r = await probeTerminology({
        text: probeText.trim(),
        source_lang: isCyrillic ? "ru" : "en",
        target_lang: isCyrillic ? "en" : "ru",
        include_translation: true,
        term: {
          source_abbreviation: picked.source_text ?? "",
          source_lang: picked.source_lang ?? "",
          source_expansion: picked.source_expansion ?? "",
          target_abbreviation: picked.target_abbreviation ?? "",
          target_lang: picked.target_lang ?? "",
          target_expansion: picked.target_text ?? "",
          domain: picked.domain ?? "",
          context_words: ctx,
        },
      });
      if (r.ok) setProbeRes(r.data);
      else toast(r.error || "Проба не удалась", "error");
    } finally {
      setProbing(false);
    }
  }

  return (
    <div>
      <SenseSearch
        allow={["abbreviation"]}
        placeholder="Аббревиатура (напр. HR)…"
        onPick={setPicked}
        pickedItemId={picked?.item_id}
      />
      {picked && detail.isLoading && (
        <div
          className="mt-muted"
          style={{ marginTop: 12, fontSize: "var(--fs-sm)" }}
        >
          Загружаю смысл…
        </div>
      )}
      {picked && item && (
        <div style={{ marginTop: 12 }}>
          <div className="mt-muted" style={{ marginBottom: 4 }}>
            Слова-маркеры этого смысла (через запятую) — по ним выбирается смысл
          </div>
          <textarea
            className="mt-input"
            rows={2}
            style={{ width: "100%", fontSize: "var(--fs-sm)" }}
            value={ctx}
            onChange={(e) => setCtx(e.target.value)}
          />
          <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              className="mt-btn mt-btn--sm mt-btn--primary"
              disabled={saveMut.isPending}
              onClick={() => saveMut.mutate()}
            >
              Сохранить контекст
            </button>
            <Link
              className="mt-btn mt-btn--sm mt-btn--ghost"
              to={`/admin/terminology/${encodeURIComponent(picked.item_id)}`}
            >
              Открыть полную карточку →
            </Link>
          </div>

          <div style={{ marginTop: 12 }}>
            <div className="mt-muted" style={{ marginBottom: 4 }}>
              Проверить на примере (учитывает несохранённое выше)
            </div>
            <textarea
              className="mt-input"
              rows={2}
              style={{ width: "100%", fontSize: "var(--fs-sm)" }}
              placeholder="Предложение (RU или EN) с этой аббревиатурой…"
              value={probeText}
              onChange={(e) => setProbeText(e.target.value)}
            />
            <button
              type="button"
              className="mt-btn mt-btn--sm mt-btn--secondary"
              disabled={probing}
              onClick={() => void probe()}
              style={{ marginTop: 6 }}
            >
              {probing ? "Проверяю…" : "Проверить"}
            </button>
            {probeRes && (
              <div
                className="rf-example"
                style={{ marginTop: 8, padding: 8, fontSize: "var(--fs-sm)" }}
              >
                {probeRes.terms.length > 0 ? (
                  <div>
                    <span className="mt-muted">сработало: </span>
                    {probeRes.terms.map((t, i) => (
                      <b key={i}>
                        {t.surface} → {t.output_abbreviation ?? t.target_text}
                        {i < probeRes.terms.length - 1 ? "; " : ""}
                      </b>
                    ))}
                  </div>
                ) : (
                  <div className="mt-muted">не сматчилось</div>
                )}
                {probeRes.translation && (
                  <div style={{ marginTop: 4, overflowWrap: "anywhere" }}>
                    <span className="mt-muted">перевод: </span>
                    <b>{probeRes.translation}</b>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
